import React, { useEffect, useMemo, useState } from "react";
import KpiStrip from "./KpiStrip";
import Tag from "./Tag";
import { money, num } from "../format";
import TablePagination from "../../../../../../../Components/Pagination/TablePagination";

const GAUGE_MAX = 120; // days shown on the cover bar

/** Days of cover as a bar, with a tick where the SKU's lead time + safety ends. */
function CoverGauge({ row }) {
  if (row.coverDays === null || row.coverDays === undefined) {
    return <span className="ia-muted">No sales</span>;
  }
  const level = row.alert.level === "crit" ? "crit" : row.alert.level === "ok" ? "ok" : "warn";
  const width = (Math.min(row.coverDays, GAUGE_MAX) / GAUGE_MAX) * 100;
  const tick = (Math.min(row.needsDays, GAUGE_MAX) / GAUGE_MAX) * 100;
  return (
    <div className="ia-cover" title={`${row.coverDays} days of cover, needs ${row.needsDays}`}>
      <div className="ia-gauge">
        <i className={`ia-gauge__fill ia-bg--${level}`} style={{ width: `${width}%` }} />
        <b className="ia-gauge__tick" style={{ left: `${tick}%` }} />
      </div>
      <span className={level === "crit" ? "ia-text--crit" : ""}>{Math.round(row.coverDays)} d</span>
    </div>
  );
}

export default function InventoryHealth({ data, currency, onOpenSku }) {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const k = data.kpis;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.rows.filter((r) => {
      if (filter === "act" && !r.actToday) return false;
      if (!["all", "act"].includes(filter) && r.filter !== filter) return false;
      if (q && !`${r.sku} ${r.name || ""} ${r.asin || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data.rows, filter, query]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filter, query, pageSize]);

  const totalPages = Math.ceil(rows.length / pageSize) || 1;
  const safeCurrentPage = Math.min(Math.max(0, currentPage), totalPages - 1);
  const startIdx = rows.length === 0 ? 0 : safeCurrentPage * pageSize + 1;
  const endIdx = Math.min((safeCurrentPage + 1) * pageSize, rows.length);

  const pagedRows = useMemo(() => {
    const start = safeCurrentPage * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, safeCurrentPage, pageSize]);

  return (
    <div>
      <p className="ia-lead">Click any row for the SKU detail. Red rows run out before new stock can arrive.</p>
      <KpiStrip
        items={[
          { label: "Stockout before restock", value: k.stockoutBeforeRestock, sub: "SKUs need action today", level: k.stockoutBeforeRestock ? "crit" : "ok" },
          { label: "Stockout risk", value: k.stockoutRisk, sub: "Within lead time + safety", level: k.stockoutRisk ? "warn" : null },
          { label: "Aged surcharge if no action", value: money(k.agedSurchargeIfNoAction, currency), sub: "Amazon's estimate" },
          { label: "Units that can't sell", value: num(k.unitsCantSell), sub: `Stranded on ${k.strandedListings} listing${k.strandedListings === 1 ? "" : "s"}` },
        ]}
      />
      <div className="ia-toolbar">
        <div className="ia-chips" role="group" aria-label="Filter SKUs">
          {data.filters.map((f) => (
            <button
              type="button"
              key={f.key}
              className="ia-chip"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <TablePagination
            pageSize={pageSize}
            totalRecords={rows.length}
            onPageChange={({ currentPage: p }) => setCurrentPage(p)}
            currentPage={safeCurrentPage}
          />
          <input
            className="ia-input"
            type="search"
            placeholder="Search SKU, ASIN or name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search SKUs"
          />
        </div>
      </div>
      <div className="ia-card">
        <div className="ia-scroll">
          <table className="ia-table">
            <thead>
              <tr>
                <th>SKU</th>
                <th className="ia-num">FBA</th>
                <th className="ia-num">Inbound</th>
                <th className="ia-num">AWD</th>
                <th>Days of cover vs. need</th>
                <th>Suggested action</th>
                <th>Alert</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ia-empty">No SKUs match. Clear the filter or search to see all.</td>
                </tr>
              ) : (
                pagedRows.map((r) => (
                  <tr
                    key={r.sku}
                    className={`ia-row${r.actToday ? " ia-row--urgent" : ""}`}
                    tabIndex={0}
                    onClick={() => onOpenSku(r.sku)}
                    onKeyDown={(e) => (e.key === "Enter" ? onOpenSku(r.sku) : null)}
                  >
                    <td>
                      <div className="ia-sku">{r.sku}</div>
                      <small className="ia-muted">{r.name}</small>
                    </td>
                    <td className="ia-num">{num(r.fba)}</td>
                    <td className="ia-num">{num(r.inbound)}</td>
                    <td className="ia-num">{num(r.awd)}</td>
                    <td>
                      <CoverGauge row={r} />
                    </td>
                    <td className={r.actToday ? "ia-text--crit ia-strong" : ""}>{r.action}</td>
                    <td>
                      <Tag level={r.alert.level}>{r.alert.label}</Tag>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* UI-only Pagination Footer */}
        {rows.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px",
              padding: "12px 16px",
              borderTop: "1px solid var(--ia-line)",
              fontSize: "13px",
              color: "var(--ia-ink-2)",
              backgroundColor: "#fafbfc",
              borderBottomLeftRadius: "var(--ia-radius)",
              borderBottomRightRadius: "var(--ia-radius)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              <span>
                Showing <strong>{startIdx}</strong> to <strong>{endIdx}</strong> of{" "}
                <strong>{rows.length}</strong> SKUs
              </span>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", margin: 0, cursor: "pointer" }}>
                <span>Rows:</span>
                <select
                  className="ia-input ia-input--small"
                  style={{
                    padding: "3px 8px",
                    cursor: "pointer",
                    height: "30px",
                    width: "auto",
                    fontWeight: 600,
                  }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  aria-label="Rows per page"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>

            <TablePagination
              pageSize={pageSize}
              totalRecords={rows.length}
              onPageChange={({ currentPage: p }) => setCurrentPage(p)}
              currentPage={safeCurrentPage}
            />
          </div>
        )}
      </div>
      <div className="ia-legend">
        <span>
          <b className="ia-gauge__tick ia-gauge__tick--legend" /> Lead time + transit + safety days
        </span>
      </div>
    </div>
  );
}
